<template>
  <div>
    <BaseModal @input="value => value === false && closeModal()" :title="modalTitle" v-model="modalOpen">
      <OrderDetail />
    </BaseModal>
    <div class="orders-container">
      <table class="order-info-table">
        <thead>
          <tr class="order-info-table__columns">
            <th>{{ $t('orders.creationTime') }}</th>
            <th>{{ $t('orders.status') }}</th>
            <th>{{ $t('orders.type') }}</th>
            <th>{{ $t('orders.details') }}</th>
            <th>
              <div class="column-header">
                <div class="column-header__name">
                  <span>{{ $t('orders.area') }}</span>
                  <span>{{ $t('orders.originZone') }}</span>
                </div>
                <button class="icon-button" @click="toggleAreaFilters">
                  <span v-if="filter.area || filter.originZone" class="material-icons">filter_alt</span>
                  <span v-else class="material-icons">filter_alt_off</span>
                </button>
                <div v-if="showColumnFilters" class="column-header__filter">
                  <button class="icon-button close-button" @click="showColumnFilters = false">
                    <span class="material-icons">close</span>
                  </button>
                  <span class="filter-label">{{ $t('orders.filter.query', { filterName: $t('orders.area') }) }}</span>
                  <v-select :options="areaFilterOptions" v-model="filter.area" :placeholder="$t('meta.pleaseSelect')" />
                  <span class="filter-label">{{
                    $t('orders.filter.query', { filterName: $t('orders.originZone') })
                  }}</span>
                  <v-select
                    :options="originZoneFilterOptions"
                    v-model="filter.originZone"
                    :placeholder="$t('meta.pleaseSelect')"
                  />
                </div>
              </div>
            </th>
            <th>{{ $t('orders.actions.name') }}</th>
          </tr>
        </thead>
        <div v-if="loading">
          <LoadingSpinner />
        </div>
        <tbody v-else>
          <tr v-for="order in orders" :key="order.id" class="order-info-table__rows" :class="[order.status]">
            <OrdersTableRow :order="order" />
          </tr>
        </tbody>
      </table>
      <div v-if="!loading && pagination && pagination.total_pages > 1" class="pagination">
        <Pagination
          v-model="page"
          @paginate="page => loadOrders({ page })"
          :records="pagination.total_objects"
          :per-page="pagination.per_page"
          :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
        />
      </div>
      <div v-else-if="orders && orders.length === 0" class="no-filter-result">
        {{ $t('orders.filter.noResult') }}
        <button v-if="canClearFilter" @click="clearFilters" class="button button-small button-round button-blue">
          {{ $t('orders.filter.clear') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { isEqual } from 'lodash'
import moment from 'moment'
import Pagination from 'vue-pagination-2'

import BaseModal from '@/components/BaseModal.vue'
import LoadingSpinner from '@/components/LoadingSpinner/index.vue'
import OrderDetail from '@/components/Orders/OrderDetail/index.vue'
import OrdersTableRow from '@/components/Orders/OrdersTable/OrdersTableRow/index.vue'

import areasApi from '@/api/areas'
import ordersApi from '@/api/orders'
import zonesApi from '@/api/zones'

export default {
  name: 'OrdersTable',
  components: {
    BaseModal,
    LoadingSpinner,
    OrderDetail,
    OrdersTableRow,
    Pagination,
  },
  props: {
    companyUuid: { type: String, required: true },
    value: { type: Object, required: true },
  },
  data: () => ({
    orders: null,
    orderDetail: null,
    loading: true,
    modalOpen: false,
    showColumnFilters: false,
    areas: null,
    zones: null,
    pagination: {},
    page: 1,
    intervalHandle: null,
  }),
  methods: {
    async loadOrders({ page, setLoading = true } = {}) {
      if (setLoading) this.loading = true
      if (page) this.page = page

      const response = await ordersApi.getPage({
        companyUuid: this.companyUuid,
        filter: {
          status: this.filter.status === 'all' ? null : this.filter.status,
          updated_after: this.filter.period === '24hrs' ? 24 : null,
          area_uuid: this.filter.area?.uuid,
          origin_zone_uuid: this.filter.originZone?.uuid,
          from: this.filter.from
            ? moment(this.filter.from).format('YYYY-MM-DD')
            : moment().subtract(1, 'weeks').format('YYYY-MM-DD'),
          to: this.filter.to ? moment(this.filter.to).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        },
        page: this.page,
      })

      // since we are live updating we only want the ui to change when there is an actual update
      if (!isEqual(this.orders, response.orders)) {
        this.orders = response.orders
        this.pagination = response.pagination
      }
      this.loading = false
    },
    toggleAreaFilters() {
      this.showColumnFilters = !this.showColumnFilters
    },
    clearFilters() {
      this.filter = {
        ...this.filter,
        area: null,
        originZone: null,
        period: 'all',
        status: 'all',
      }
      this.showColumnFilters = false
    },
    closeModal() {
      this.$router.push({
        path: '/orders',
        query: this.$router.currentRoute.query,
      })
      this.loadOrders()
    },
    async handleModal() {
      if (!this.$route.params.orderUuid) return
      this.orderDetail = await ordersApi.get(this.$route.params.orderUuid)
      this.modalOpen = true
    },
  },
  computed: {
    canClearFilter() {
      const { period, status, area, originZone } = this.filter

      return period !== 'all' || status !== 'all' || area || originZone
    },
    filter: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    areaFilterOptions() {
      return [...new Set(this.areas.map(({ name, uuid }) => ({ label: name, uuid })))]
    },
    originZoneFilterOptions() {
      return [...new Set(this.zones.map(({ name, uuid }) => ({ label: name, uuid })))]
    },
    modalTitle() {
      return `${this.orderDetail?.human_type}: ${this.orderDetail?.uuid}`
    },
  },
  watch: {
    filter: {
      deep: true,
      handler() {
        this.loadOrders({ page: 1 }) // restart pagination when changing filter
      },
    },
    '$route.params.orderUuid': {
      deep: true,
      handler() {
        this.handleModal()
      },
    },
  },
  async mounted() {
    this.areas = await areasApi.getAllForCompany(this.companyUuid)
    this.zones = await zonesApi.getAllForCompany(this.companyUuid)
    this.loadOrders({ page: 1 })
    this.handleModal()
    this.intervalHandle = setInterval(() => {
      this.loadOrders({ setLoading: false })
    }, 10000) // reload every 10 seconds
  },
  beforeDestroy() {
    clearInterval(this.intervalHandle)
  },
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
